import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Form, Button, Badge, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLightbulb,
  faWandMagicSparkles
} from "@fortawesome/free-solid-svg-icons";
import VariantPreview from "./variant-preview";
import {
  TestCard,
  TextValue,
  SectionTitle,
  VariantLabel,
  VariantColumn
} from "./split-test.styles";
import {
  formatNumber,
  formatPercent,
  sampleSizePerVariant,
  describeValue,
  elementTypeOf,
  ELEMENT_TYPES
} from "./split-test.helpers";
import { fetchSplitTestBaseline } from "../../services/api-declaration";
import { hasAccess } from "../../services/access";
import { ACCESS_CONFIG } from "../strings";

const MDE_OPTIONS = [0.05, 0.1, 0.15, 0.2];
const ASSUMED_BASELINE = 0.35;

// Next proposal: AI or manual, editable before the start.
// save enablement per element type: text needs a non-empty text that differs
// from the control, colour a #rrggbb that differs, feature an enabled variant
// (social proof additionally needs the {{count}} placeholder)
const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const canSave = (form, typeOf) => {
  if (!form) return false;
  const type = typeOf(form.element);
  if (type === ELEMENT_TYPES.FEATURE) {
    if (!form.variantValue?.enabled) return false;
    if (form.element === "socialProof") {
      return /\{\{count\}\}/.test(form.variantValue.template || "");
    }
    return true;
  }
  if (typeof form.variantValue !== "string") return false;
  if (type === ELEMENT_TYPES.COLOR) {
    return (
      HEX_RE.test(form.variantValue) &&
      form.variantValue.toLowerCase() !==
        String(form.controlValue || "").toLowerCase()
    );
  }
  return !!form.variantValue.trim() && form.variantValue !== form.controlValue;
};

const ProposalCard = ({
  proposal,
  elements,
  funnelType,
  hasRunning,
  busy,
  onPropose,
  onSave,
  onStart,
  onCreateManual
}) => {
  const { t } = useTranslation();
  const canAct = hasAccess(ACCESS_CONFIG.ADMIN_TIER);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState(null);

  const elementLabel = (key) =>
    elements?.find((e) => e.key === key)?.label || key;
  const typeOf = (key) => elementTypeOf(elements, key);

  const startEditing = (source) => {
    const element = source?.element || elements?.[0]?.key || "submitButton";
    const type = typeOf(element);
    setForm({
      element,
      controlValue:
        source?.controlValue ??
        (type === ELEMENT_TYPES.FEATURE ? { enabled: false } : ""),
      variantValue:
        source?.variantValue ??
        (type === ELEMENT_TYPES.FEATURE ? { enabled: true, template: "" } : ""),
      hypothesis: source?.hypothesis ?? "",
      rationale: source?.rationale ?? "",
      mdeRelative: source?.mdeRelative ?? 0.1
    });
    setEditing(true);
  };

  useEffect(() => {
    if (!editing || !form?.element) return;
    let cancelled = false;
    fetchSplitTestBaseline({ funnelType, element: form.element })
      .then((resp) => {
        if (cancelled) return;
        const data = resp.data.data;
        setBaseline(data);
        // default: the most common NON-empty text (funnels without a text
        // cannot be tested, see backend pickControlValue)
        const type = data.type || ELEMENT_TYPES.TEXT;
        const firstText =
          type === ELEMENT_TYPES.TEXT
            ? data.values?.find((v) => v.value?.trim())?.value
            : data.values?.[0]?.value;
        setForm((prev) => {
          if (!prev) return prev;
          const empty =
            type === ELEMENT_TYPES.FEATURE
              ? !prev.controlValue || typeof prev.controlValue !== "object"
              : !prev.controlValue;
          if (!empty || firstText === undefined) return prev;
          const next = { ...prev, controlValue: firstText };
          if (
            type === ELEMENT_TYPES.FEATURE &&
            data.template &&
            !prev.variantValue?.template
          ) {
            next.variantValue = { enabled: true, template: data.template };
          }
          return next;
        });
      })
      .catch(() => setBaseline(null));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, form?.element, funnelType]);

  const submitForm = async () => {
    if (proposal) {
      await onSave(proposal._id, form);
    } else {
      await onCreateManual(form);
    }
    setEditing(false);
  };

  const mde = form?.mdeRelative ?? proposal?.mdeRelative ?? 0.1;
  const estimate = sampleSizePerVariant(ASSUMED_BASELINE, mde);

  if (!proposal && !editing) {
    return (
      <TestCard className="p-3 mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <div className="fw-bold fs-5">
              <FontAwesomeIcon
                icon={faLightbulb}
                className="text-warning me-2"
              />
              {t("splitTests.proposal.title")}
            </div>
            <div className="text-muted small">
              {hasRunning
                ? t("splitTests.proposal.waitForRunning")
                : t("splitTests.proposal.empty")}
            </div>
          </div>
          {canAct && (
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="primary"
                disabled={busy || hasRunning}
                onClick={onPropose}
              >
                <FontAwesomeIcon icon={faWandMagicSparkles} className="me-1" />
                {t("splitTests.actions.propose")}
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={busy || hasRunning}
                onClick={() => startEditing(null)}
              >
                {t("splitTests.actions.createManual")}
              </Button>
            </div>
          )}
        </div>
      </TestCard>
    );
  }

  if (editing && form) {
    const maxLength = elements?.find((e) => e.key === form.element)?.maxLength;
    return (
      <TestCard className="p-3 mb-4">
        <div className="fw-bold fs-5 mb-3">
          {proposal
            ? t("splitTests.proposal.editTitle")
            : t("splitTests.proposal.manualTitle")}
        </div>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>{t("splitTests.fields.element")}</Form.Label>
              <Form.Select
                value={form.element}
                onChange={(e) => {
                  // values are typed: drop the old ones so a text does not
                  // survive as a colour or feature variant
                  const nextType = typeOf(e.target.value);
                  setForm({
                    ...form,
                    element: e.target.value,
                    // empty control: the baseline effect fills it (and the
                    // feature template) for the new element
                    controlValue: "",
                    variantValue:
                      nextType === ELEMENT_TYPES.FEATURE
                        ? { enabled: true, template: "" }
                        : "",
                    // the AI rationale belongs to the proposed element
                    rationale: ""
                  });
                }}
              >
                {[
                  ELEMENT_TYPES.TEXT,
                  ELEMENT_TYPES.COLOR,
                  ELEMENT_TYPES.FEATURE
                ].map((group) => (
                  <optgroup key={group} label={t(`splitTests.types.${group}`)}>
                    {elements
                      ?.filter(
                        (el) => (el.type || ELEMENT_TYPES.TEXT) === group
                      )
                      .map((el) => (
                        <option key={el.key} value={el.key}>
                          {el.label}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </Form.Select>
            </Form.Group>
            {typeOf(form.element) === ELEMENT_TYPES.COLOR && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>{t("splitTests.fields.controlColor")}</Form.Label>
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Control
                      type="color"
                      style={{ width: 48 }}
                      value={form.controlValue || "#e15d3a"}
                      disabled
                      readOnly
                    />
                    <Form.Control
                      value={form.controlValue || ""}
                      disabled
                      readOnly
                    />
                  </div>
                  <div className="form-text">
                    {t("splitTests.proposal.colorControlHint")}
                  </div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{t("splitTests.fields.variantColor")}</Form.Label>
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Control
                      type="color"
                      style={{ width: 48 }}
                      value={
                        /^#[0-9a-fA-F]{6}$/.test(form.variantValue || "")
                          ? form.variantValue
                          : "#1f7a3a"
                      }
                      onChange={(e) =>
                        setForm({ ...form, variantValue: e.target.value })
                      }
                    />
                    <Form.Control
                      placeholder="#rrggbb"
                      value={form.variantValue || ""}
                      onChange={(e) =>
                        setForm({ ...form, variantValue: e.target.value })
                      }
                    />
                  </div>
                </Form.Group>
              </>
            )}
            {typeOf(form.element) === ELEMENT_TYPES.FEATURE && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>{t("splitTests.fields.controlValue")}</Form.Label>
                  <div className="text-muted small">
                    {t("splitTests.proposal.featureControlHint")}
                  </div>
                </Form.Group>
                {form.element === "socialProof" && (
                  <Form.Group className="mb-3">
                    <Form.Label>{t("splitTests.fields.template")}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      maxLength={160}
                      value={form.variantValue?.template || ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          variantValue: {
                            enabled: true,
                            template: e.target.value
                          }
                        })
                      }
                    />
                    <div className="form-text">
                      {t("formDefaults.templateHint")}
                    </div>
                  </Form.Group>
                )}
                {baseline?.eligibleWithData !== undefined && (
                  <div className="form-text mb-3">
                    {t("splitTests.proposal.featureEligible", {
                      count: baseline.eligibleWithData
                    })}
                  </div>
                )}
              </>
            )}
            {typeOf(form.element) === ELEMENT_TYPES.TEXT && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {t("splitTests.fields.controlValue")}{" "}
                    {baseline?.total ? (
                      <span className="text-muted small">
                        (
                        {t("splitTests.proposal.baselineHint", {
                          count: baseline.total
                        })}
                        )
                      </span>
                    ) : null}
                  </Form.Label>
                  {baseline?.values?.length ? (
                    <Form.Select
                      className="mb-2"
                      value={form.controlValue}
                      onChange={(e) =>
                        setForm({ ...form, controlValue: e.target.value })
                      }
                    >
                      {baseline.values.map((v) => (
                        <option key={v.value} value={v.value}>
                          {(v.value || "(leer)").slice(0, 70)} · {v.count}{" "}
                          {t("splitTests.proposal.funnels")}
                        </option>
                      ))}
                    </Form.Select>
                  ) : null}
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={form.controlValue}
                    onChange={(e) =>
                      setForm({ ...form, controlValue: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {t("splitTests.fields.variantValue")}{" "}
                    {maxLength && typeof form.variantValue === "string" ? (
                      <span className="text-muted small">
                        ({form.variantValue.length}/{maxLength})
                      </span>
                    ) : null}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    maxLength={maxLength}
                    value={form.variantValue}
                    onChange={(e) =>
                      setForm({ ...form, variantValue: e.target.value })
                    }
                  />
                </Form.Group>
              </>
            )}
            <Form.Group className="mb-3">
              <Form.Label>{t("splitTests.fields.hypothesis")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.hypothesis}
                onChange={(e) =>
                  setForm({ ...form, hypothesis: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("splitTests.fields.mde")}</Form.Label>
              <div className="d-flex gap-2 flex-wrap">
                {MDE_OPTIONS.map((opt) => (
                  <Button
                    key={opt}
                    size="sm"
                    variant={
                      form.mdeRelative === opt ? "primary" : "outline-secondary"
                    }
                    onClick={() => setForm({ ...form, mdeRelative: opt })}
                  >
                    +{Math.round(opt * 100)} %
                  </Button>
                ))}
              </div>
              <div className="form-text">
                {t("splitTests.proposal.sampleEstimate", {
                  n: formatNumber(
                    sampleSizePerVariant(ASSUMED_BASELINE, form.mdeRelative)
                  ),
                  rate: formatPercent(ASSUMED_BASELINE, 0)
                })}
              </div>
            </Form.Group>
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                disabled={busy || !canSave(form, typeOf)}
                onClick={submitForm}
              >
                {t("common.save", { defaultValue: "Speichern" })}
              </Button>
              <Button
                variant="outline-secondary"
                disabled={busy}
                onClick={() => setEditing(false)}
              >
                {t("common.cancel", { defaultValue: "Abbrechen" })}
              </Button>
            </div>
          </Col>
          <Col md={6}>
            <Row className="g-2">
              <Col sm={6}>
                <VariantLabel $variant="A">
                  <span className="chip">A</span>
                  {t("splitTests.variant.A")}
                </VariantLabel>
                <VariantPreview
                  element={form.element}
                  value={form.controlValue}
                  funnelType={funnelType}
                />
              </Col>
              <Col sm={6}>
                <VariantLabel $variant="B">
                  <span className="chip">B</span>
                  {t("splitTests.variant.B")}
                </VariantLabel>
                <VariantPreview
                  element={form.element}
                  value={form.variantValue}
                  funnelType={funnelType}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </TestCard>
    );
  }

  return (
    <TestCard className="p-3 mb-4">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faLightbulb} className="text-warning" />
            <span className="fw-bold fs-5">
              {t("splitTests.proposal.title")}
            </span>
            <Badge bg={proposal.source === "AI" ? "info" : "secondary"}>
              {proposal.source === "AI"
                ? t("splitTests.proposal.sourceAi")
                : t("splitTests.proposal.sourceManual")}
            </Badge>
          </div>
          <div className="text-muted small mt-1">
            {elementLabel(proposal.element)} ·{" "}
            {t("splitTests.running.eligible", {
              count: proposal.eligibleFunnelCount ?? 0
            })}{" "}
            ·{" "}
            {t("splitTests.proposal.mdeShort", {
              mde: Math.round((proposal.mdeRelative || 0.1) * 100)
            })}{" "}
            ·{" "}
            {t("splitTests.proposal.sampleShort", {
              n: formatNumber(estimate)
            })}
          </div>
        </div>
        {canAct && (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={busy || hasRunning}
              onClick={onPropose}
            >
              <FontAwesomeIcon icon={faWandMagicSparkles} className="me-1" />
              {t("splitTests.actions.proposeAgain")}
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={busy}
              onClick={() => startEditing(proposal)}
            >
              {t("splitTests.actions.edit")}
            </Button>
            <Button
              size="sm"
              variant="success"
              disabled={busy || hasRunning || !proposal.eligibleFunnelCount}
              onClick={() => onStart(proposal)}
            >
              {t("splitTests.actions.start")}
            </Button>
          </div>
        )}
      </div>
      {hasRunning && (
        <Alert variant="light" className="mt-3 mb-0 py-2 small border">
          {t("splitTests.proposal.waitForRunning")}
        </Alert>
      )}
      {!hasRunning && !proposal.eligibleFunnelCount && (
        <Alert variant="warning" className="mt-3 mb-0 py-2 small">
          {t("splitTests.proposal.noEligible")}
        </Alert>
      )}
      <Row className="mt-3 g-3">
        <Col md={5}>
          <SectionTitle>{t("splitTests.fields.hypothesis")}</SectionTitle>
          <div className="mb-3">{proposal.hypothesis || "–"}</div>
          <SectionTitle>{t("splitTests.fields.rationale")}</SectionTitle>
          <div className="text-muted">{proposal.rationale || "–"}</div>
        </Col>
        <Col md={7}>
          <Row className="g-2">
            {[
              { key: "A", value: proposal.controlValue },
              { key: "B", value: proposal.variantValue }
            ].map(({ key, value }) => (
              <Col sm={6} key={key}>
                <VariantColumn>
                  <VariantLabel $variant={key}>
                    <span className="chip">{key}</span>
                    {t(`splitTests.variant.${key}`)}
                  </VariantLabel>
                  <TextValue className="mt-2">
                    {typeOf(proposal.element) === ELEMENT_TYPES.COLOR && (
                      <span
                        className="d-inline-block me-2 align-middle rounded"
                        style={{ width: 16, height: 16, background: value }}
                      />
                    )}
                    {describeValue(typeOf(proposal.element), value, t)}
                  </TextValue>
                  <VariantPreview
                    element={proposal.element}
                    value={value}
                    funnelType={funnelType}
                  />
                </VariantColumn>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </TestCard>
  );
};

export default ProposalCard;
